import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TaskSquare } from "iconsax-react";
import { Input, Button, Select, SearchableSelect, ModalForm, DatePicker, Prompt, ToggleButton } from "@/shared/ui";
import type { Assignee, BoardProject, Task, TaskListItem, TaskStatus } from "../../../types";
import { PRIORITY_MAP, TASK_STATUSES, TASK_STATUS_MAP } from "../../../consts";
import { useTaskForm } from "../../../hooks";
import TagInput from "./TagInput";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  isRefetching: boolean;
  defaultStatus: TaskStatus | null;
  mode: "create" | "edit";
  task?: Task | null;
  assignees: Assignee[];
  projectId: number;
  kpiTasks: TaskListItem[];
  projects?: BoardProject[];
  forceKpi?: boolean;
}

export default function TaskFormModal({
  onClose,
  onSuccess,
  isRefetching,
  defaultStatus,
  mode,
  task,
  assignees,
  projectId: propProjectId,
  kpiTasks,
  projects,
  forceKpi = false,
}: Props) {
  const { t, i18n } = useTranslation("TasksPage");
  const { t: tBoard } = useTranslation("BoardPage");
  const locale = (i18n.language as "ru" | "kk" | "en") || "ru";
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(propProjectId || null);
  const isCrossProject = propProjectId === 0 && projects && projects.length > 0;
  const effectiveProjectId = isCrossProject ? (selectedProjectId ?? 0) : propProjectId;

  const { register, watch, setValue, errors, isSubmitting, errorPrompt, handleCloseErrorPrompt, handleSubmit } =
    useTaskForm({
      projectId: effectiveProjectId,
      defaultStatus,
      onSuccess,
      mode,
      task,
      assignees,
      kpiTasks,
      forceKpi,
    });

  const isLoading = isSubmitting || isRefetching;
  const titleValue = watch("title");
  const canSubmit = !isLoading && (!isCrossProject || selectedProjectId !== null) && !!titleValue?.trim();
  const isStatusLocked = mode === "create" && defaultStatus !== null;

  const statusOptions = TASK_STATUSES.map((status) => ({
    label: t(TASK_STATUS_MAP[status].label),
    value: status,
  }));

  const priorityOptions = Object.entries(PRIORITY_MAP).map(([value, info]) => ({
    label: t(info.label),
    value: value as "low" | "normal" | "high" | "critical",
  }));

  const isGoal = forceKpi;
  const title = isGoal
    ? mode === "create" ? t("taskForm.goalTitleCreate") : t("taskForm.goalTitleEdit")
    : mode === "create" ? t("taskForm.titleCreate") : t("taskForm.titleEdit");
  const subtitle = isGoal
    ? mode === "create" ? t("taskForm.subtitleGoalCreate") : t("taskForm.subtitleGoalEdit")
    : mode === "create" ? t("taskForm.subtitleTaskCreate") : t("taskForm.subtitleTaskEdit");
  const submitLabel = isGoal && mode === "create"
    ? t("taskForm.actions.createGoal")
    : mode === "create" ? t("taskForm.actions.create") : t("taskForm.actions.save");

  const selectedAssignee = assignees.find((a) => a.id === watch("assigned_to")) || null;
  const isKpi = watch("is_kpi");

  useEffect(() => {
    if (forceKpi && mode === "create") {
      setValue("is_kpi", true, { shouldValidate: true });
      setValue("parent_task", null, { shouldValidate: true });
      setValue("status", "in_progress", { shouldValidate: true });
      setValue("priority", "normal", { shouldValidate: true });
    }
  }, [forceKpi, mode, setValue]);

  const kpiOptions = kpiTasks.map((kpi) => ({
    id: kpi.id,
    name: kpi.title,
  }));
  const selectedParent = kpiOptions.find((k) => k.id === watch("parent_task")) || null;

  return (
    <>
      <ModalForm
        icon={TaskSquare}
        onClose={onClose}
        footer={(close) => (
          <div className="grid grid-cols-[2fr_3fr] gap-3">
            <Button variant="secondary" size="huge" onClick={close} type="button" disabled={isLoading}>
              {t("taskForm.actions.cancel")}
            </Button>
            <Button
              variant="primary"
              size="huge"
              type="submit"
              form="task-form"
              disabled={!canSubmit}
              fullWidthTooltip
              textDuringDisabled={
                isCrossProject && selectedProjectId === null ? t("taskForm.rules.selectProject") : undefined
              }>
              {isLoading ? t("taskForm.actions.submitting") : submitLabel}
            </Button>
          </div>
        )}>
        <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke">
          <h4 className="text-display-2xs content-base-primary">{title}</h4>
          <p className="text-body-regular-sm content-base-secondary">{subtitle}</p>
        </div>

        <form id="task-form" onSubmit={handleSubmit} className="flex flex-col gap-4 mt-5">
          {isCrossProject && (
            <Select
              label={tBoard("crossProject.projectLabel")}
              placeholder={tBoard("crossProject.selectProject")}
              options={projects.map((p) => ({ label: p.name, value: String(p.id) }))}
              value={selectedProjectId !== null ? String(selectedProjectId) : null}
              onChange={(v) => setSelectedProjectId(v ? Number(v) : null)}
              hasRequiredLabel
            />
          )}

          <Input
            label={t("taskForm.fields.title")}
            placeholder={t("taskForm.placeholders.title")}
            hasRequiredLabel
            {...register("title")}
            error={errors.title?.message ? t(errors.title.message) : undefined}
          />

          <Input
            isTextarea
            label={t("taskForm.fields.description")}
            placeholder={t("taskForm.placeholders.description")}
            {...register("description")}
            error={errors.description?.message ? t(errors.description.message) : undefined}
          />

          {!isGoal && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Select
                  label={t("taskForm.fields.status")}
                  placeholder={t("taskForm.placeholders.status")}
                  options={statusOptions}
                  value={watch("status")}
                  onChange={(v) => setValue("status", v ?? "todo", { shouldValidate: true })}
                  disabled={isStatusLocked}
                  error={errors.status?.message ? t(errors.status.message) : undefined}
                  hasRequiredLabel
                />

                <Select
                  label={t("taskForm.fields.priority")}
                  options={priorityOptions}
                  value={watch("priority")}
                  onChange={(v) => setValue("priority", v ?? "normal", { shouldValidate: true })}
                  hasRequiredLabel
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <SearchableSelect
                  label={t("taskForm.fields.assignee")}
                  placeholder={t("taskForm.placeholders.assignee")}
                  searchPlaceholder={t("taskForm.placeholders.searchAssignee")}
                  options={assignees}
                  value={selectedAssignee}
                  onChange={(v) => setValue("assigned_to", v?.id ?? null, { shouldValidate: true })}
                  searchKeys={["name"]}
                  displayKey="name"
                />

                <DatePicker
                  mode="single"
                  label={t("taskForm.fields.dueDate")}
                  locale={locale}
                  direction="top"
                  value={watch("due_date") ? new Date(watch("due_date")!) : null}
                  onChange={(date) =>
                    setValue("due_date", date ? date.toISOString().split("T")[0] : "", {
                      shouldValidate: true,
                    })
                  }
                />
              </div>

              <div className="flex justify-between items-center gap-5">
                <span className="text-label-sm content-base-primary">{t("taskForm.fields.isKpiToggle")}</span>
                <ToggleButton
                  checked={isKpi}
                  onChange={(checked) => {
                    setValue("is_kpi", checked, { shouldValidate: true });
                    if (!checked) {
                      setValue("parent_task", null, { shouldValidate: true });
                    }
                  }}
                />
              </div>

              {isKpi && kpiOptions.length > 0 && (
                <SearchableSelect
                  label={t("taskForm.fields.parentTask")}
                  placeholder={t("taskForm.placeholders.parentTask")}
                  searchPlaceholder={t("taskForm.placeholders.parentTask")}
                  options={kpiOptions}
                  value={selectedParent}
                  onChange={(v) => setValue("parent_task", v?.id ?? null, { shouldValidate: true })}
                  searchKeys={["name"]}
                  displayKey="name"
                  error={errors.parent_task?.message ? t(errors.parent_task.message) : undefined}
                />
              )}

              <div className="flex justify-between items-center gap-5">
                <span className="text-label-sm content-base-primary">{t("taskForm.fields.requiresFsmReportToggle")}</span>
                <ToggleButton
                  checked={watch("requires_fsm_report")}
                  onChange={(checked) => {
                    setValue("requires_fsm_report", checked, { shouldValidate: true });
                  }}
                />
              </div>

              <TagInput
                label={t("taskForm.fields.tags")}
                placeholder={t("taskForm.placeholders.tags")}
                value={watch("tags") || []}
                onChange={(v) => setValue("tags", v, { shouldValidate: true })}
                addLabel={t("taskForm.actions.addTag")}
              />
            </>
          )}
        </form>
      </ModalForm>

      {errorPrompt && (
        <Prompt
          onClose={handleCloseErrorPrompt}
          title={errorPrompt.title}
          text={errorPrompt.text}
          variant="error"
          namespace="TasksPage"
        />
      )}
    </>
  );
}
